<template>
  <ankh-list>
    <template #btn-group>
      <el-button type="primary" @click.native="handleAdd">{{ $t('新增') }}</el-button>
      <el-button @click.native="handleBatchDelete">{{ $t('批量删除') }}</el-button>
    </template>

    <!-- 搜索表单区：遵循系统统一表单间距、大小与布局 -->
    <template #search-form>
      <!-- 区域名称搜索 -->
      <el-form-item :label="$t('区域名称')">
        <el-input v-model="searchForm.name" clearable :placeholder="$t('请输入区域名称')"/>
      </el-form-item>
      <!-- 操作按钮与统一交互 -->
      <el-form-item>
        <el-button type="primary" size="small" @click="$refs.list.handleSearch()">{{ $t('搜索') }}</el-button>
        <el-button size="small" @click="$refs.list.reset()">{{ $t('重置') }}</el-button>
      </el-form-item>
    </template>

    <!-- 表格区：系统封装TableList，接入高度传递、选择与查询事件 -->
    <template #table="{height}">
      <table-list
          ref="list"
          :height="height"
          @query="handleSearch"
          :search-params="searchForm"
          :data="tableData"
          @selection-change="selection = $event">

        <el-table-column type="selection" width="50"/>
        <el-table-column type="index" :label="$t('序号')" width="60"/>

        <el-table-column prop="name" :label="$t('区域名称')" show-overflow-tooltip />
        <el-table-column prop="sort" :label="$t('排序')" align="center">
          <template v-slot="{row}">
            <el-tag type="info">{{ row.sort }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('创建时间')">
          <template v-slot="{row}">
            {{ row.createTime | formatDate }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('操作')" fixed="right" align="center">
          <template v-slot="{row}">
            <div class="fb-link-flex">
              <el-link :underline="false" type="primary" @click="handleEdit(row)">{{ $t('编辑') }}</el-link>
              <el-link :underline="false" type="danger" @click="handleDelete(row)">{{ $t('删除') }}</el-link>
            </div>
          </template>
        </el-table-column>

      </table-list>
    </template>

    <!-- 弹窗区：预留新增/编辑表单弹窗位点，保持统一行为与间距 -->
    <template #other>
      <el-dialog :visible.sync="showDialog" :title="dialogTitle" :close-on-click-modal="false" width="500px"
                 @close="resetDialog" class="geo-dialog">
        <el-form :model="form" :rules="rules" ref="form" label-width="80px" class="compact-form">
          <el-form-item :label="$t('区域名称')" prop="name">
            <el-input v-model="form.name" :placeholder="$t('请输入区域名称')"/>
          </el-form-item>
          <el-form-item :label="$t('排序')" prop="sort">
            <el-input-number
                v-model="form.sort"
                :min="1"
                :max="9999"
                :placeholder="$t('请输入排序值')"
                style="width: 100%"
            />
            <div class="tip-box">
              <span>{{ $t('排序值越小，显示越靠前') }}</span>
            </div>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showDialog = false">{{ $t('取消') }}</el-button>
          <el-button type="primary" @click="handleSubmit">{{ $t('确定') }}</el-button>
        </span>
      </el-dialog>
    </template>
  </ankh-list>
</template>

<script>
import AnkhList from '@/components/list/AnkhList.vue'
import TableList from '@/components/table-list/TableList'
import {request, requestByTip} from '@/config/request'

export default {
  name: 'GeoList',
  components: {AnkhList, TableList},
  data() {
    return {
      // 搜索表单
      searchForm: {
        name: '',
      },
      // 表格数据
      tableData: [],
      // 选择项
      selection: [],
      // 弹窗
      showDialog: false,
      isEdit: false,
      form: {
        code: '', // 主键
        name: '',
        sort: 1,
      },
      // 表单校验规则
      rules: {
        name: [{required: true, message: this.$t('请输入区域名称'), trigger: 'blur'}],
        sort: [{required: true, message: this.$t('请输入排序值'), trigger: 'blur'}],
      },
    }
  },
  methods: {
    // 查询列表：保持与系统TableList事件对接
    async handleSearch(params, setTotal) {
      try {
        // 合并搜索与分页参数；request封装会过滤空值
        const query = Object.assign({}, params)
        const resp = await request(this.$api.geo.list, query)
        this.tableData = resp.geos || []
        setTotal(resp.pager?.itemCount || resp.total);
      } catch (e) {
        // 静默失败，交由request内部提示
      }
    },
    // 新增
    handleAdd() {
      this.isEdit = false
      this.showDialog = true
      this.form = {
        code: '', // 新增时code为空
        name: '',
        sort: 1
      }
    },
    // 编辑
    handleEdit(row) {
      this.isEdit = true
      this.showDialog = true
      this.form = Object.assign({}, row)
    },
    // 删除
    async handleDelete(row) {
      try {
        await requestByTip(this.$api.geo.delete, {code: row.code})
        this.$message.success(this.$t('操作成功'))
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 批量删除
    async handleBatchDelete() {
      if (!this.selection || !this.selection.length) {
        this.$message.warning(this.$t('请选择需要删除的记录'))
        return
      }
      try {
        const codes = this.selection.map(it => it.code).filter(Boolean)
        if (!codes.length) {
          this.$message.error(this.$t('所选记录缺少主键code'))
          return
        }
        await requestByTip(
            this.$api.geo.batchDelete,
            codes,
            {isJson: true},
            {message: this.$t('您现在正在进行批量删除操作, 是否继续?')}
        )
        this.$message.success(this.$t('操作成功'))
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 提交（新增/编辑）
    async handleSubmit() {
      try {
        await this.$refs.form.validate()

        // 构建提交数据，严格按照系统现有请求方式
        const submitData = {
          name: this.form.name, // 名称
          sort: this.form.sort, // 排序
        }

        // 编辑时需要携带code主键
        if (this.isEdit && this.form.code) {
          submitData.code = this.form.code
        }

        // 使用geo接口
        const api = this.isEdit ? this.$api.geo.update : this.$api.geo.add
        await request(api, submitData)
        this.$message.success(this.$t('保存成功'))
        this.showDialog = false
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 重置弹窗
    resetDialog() {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate && this.$refs.form.clearValidate()
      })
    },
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? this.$t('编辑区域') : this.$t('新增区域')
    }
  }
}
</script>

<style scoped>
.fb-link-flex {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.geo-dialog .el-dialog {
  border-radius: 8px;
}

.geo-dialog .el-dialog__header {
  text-align: left;
  padding: 20px 20px 10px;
}

.geo-dialog .el-dialog__title {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.geo-dialog .el-dialog__title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 24px;
  background: #0077F7;
  position: absolute;
  top: 20px;
  left: 0;
}

.geo-dialog .el-dialog__body {
  padding: 10px 20px 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.geo-dialog .compact-form .el-form-item {
  margin-bottom: 18px;
}

.geo-dialog .compact-form .el-form-item__label {
  font-weight: 500;
  color: #303133;
}

.geo-dialog .dialog-footer {
  text-align: right;
  padding: 10px 20px 20px;
}

.geo-dialog .dialog-footer .el-button {
  margin-left: 10px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .geo-dialog .el-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
}

.tip-box {
  font-size: 14px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  line-height: 22px;
  color: #909399;
}
</style>
